import type { MutableRefObject } from 'react';
import React from 'react';
import { HBox, VBox } from '../../layout';
import { Drawer, Tool } from '../../basic';
import { intl } from '../../../lib';
import { Direction } from '../../common-types';
import type { MessageReaction, ReactionClickHandler } from '../chatTypes';
import type { EmojiData } from 'emoji-picker-react/src/types/exposedTypes';
import { ReactionTool } from './ReactionTool';
import { ReactionDetails } from './ReactionDetails';

export type ChatMessageReactionsProps = {
  reactions: Array<MessageReaction> | undefined;
  eventId: string | undefined;
  roomId?: string;
  emojiData: EmojiData;
  reactionClick: ReactionClickHandler;
  isTouchDevice: boolean;
  reactionRefs: MutableRefObject<(HTMLButtonElement | null)[]>;
  reactionDrawerOpen: boolean;
  setReactionDrawerOpen: (open: boolean) => void;
  reactionsDisabled?: boolean;
};

function reactionCount(reactions: MessageReaction[]): number {
  let counter = 0;
  reactions.forEach(reaction => {
    counter += reaction.count || 0;
  });
  return counter;
}

/** Reaction pills plus the touch drawer listing reactors. */
export function ChatMessageReactions({
  reactions,
  eventId,
  roomId,
  emojiData,
  reactionClick,
  isTouchDevice,
  reactionRefs,
  reactionDrawerOpen,
  setReactionDrawerOpen,
  reactionsDisabled = false
}: Readonly<ChatMessageReactionsProps>): React.JSX.Element | null {
  if (!reactions?.length || !eventId) {
    return null;
  }

  return (
    <>
      <HBox className="chat-message-reactions" wrap="wrap">
        {reactions.map((reaction, index) => (
          <ReactionTool
            reaction={reaction}
            key={reaction.key}
            eventId={eventId}
            roomId={roomId}
            emojiData={emojiData}
            reactionClick={reactionClick}
            openDrawer={() => setReactionDrawerOpen(true)}
            action={isTouchDevice ? 'openDrawer' : 'react'}
            disabled={reactionsDisabled}
            tabIndex={-1}
            buttonRef={element => {
              reactionRefs.current[index] = element;
            }}
          />
        ))}
      </HBox>
      {reactionDrawerOpen && (
        <Drawer
          className="chat-message-reaction-drawer"
          onOverlayClicked={() => setReactionDrawerOpen(false)}
          direction={Direction.BOTTOM}
          tool={
            <Tool
              onClick={() => setReactionDrawerOpen(false)}
              icon="shui-cancel"
              tooltip={intl('view.button.close')}
            />
          }
          title={
            intl('common.chatmessage.reactions.title') +
            ': ' +
            reactionCount(reactions)
          }
          enableFocusTrap={true}
        >
          <HBox align="start" wrap="wrap">
            {reactions.map(reaction => (
              <ReactionTool
                reaction={reaction}
                key={reaction.key}
                eventId={eventId}
                roomId={roomId}
                emojiData={emojiData}
                reactionClick={reactionClick}
                openDrawer={() => setReactionDrawerOpen(true)}
                action="react"
                disabled={reactionsDisabled}
              />
            ))}
          </HBox>
          <VBox className="reaction-detail-wrapper">
            {reactions.map(reaction => (
              <ReactionDetails
                reaction={reaction}
                key={reaction.key}
                showEmoji={true}
                emojiData={emojiData}
              />
            ))}
          </VBox>
        </Drawer>
      )}
    </>
  );
}
